import { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  getStreamingByMatch,
  createStreamingByMatch,
  updateStreamingByMatch,
  deleteStreamingByMatch,
} from "../../../services/streamingService";
import { getPlayers } from "../../../services/playerService";

const useStreamingData = ({ matchId, tournamentId, user, accessToken }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [dateTime, setDateTime] = useState(null);
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [url, setUrl] = useState("");
  const [isUrlSaved, setIsUrlSaved] = useState(false);
  const [matchStreamingId, setMatchStreamingId] = useState(null);
  const [streamerName, setStreamerName] = useState("");
  const [isStreamer, setIsStreamer] = useState(false);

  useEffect(() => {
    if (user) {
      const tournamentHistory = Array.isArray(user.tournamentHistory)
        ? user.tournamentHistory
        : [];

      setIsStreamer(
        tournamentHistory.some(
          (t) => t.tournamentId === tournamentId && t.isStreamer === true
        )
      );
    } else {
      setIsStreamer(false);
    }

    const fetchStreamingData = async () => {
      try {
        const data = await getStreamingByMatch(matchId, accessToken);
        if (data && data.streamingType) {
          setMatchStreamingId(data.matchStreamingId);
          let option = null;
          if (data.streamingType === "LIVE") {
            option = "directo";
          } else if (data.streamingType === "LIVE-VOD") {
            option = "diferido";
          } else if (data.streamingType === "VOD") {
            option = "transmitido";
          }
          setSelectedOption(option);

          if (option === "directo" || option === "diferido") {
            const date = dayjs(data.streamingDate);
            setDateTime(date);
            setShowDateTimePicker(false);
          } else if (option === "transmitido") {
            setUrl(data.streamingLink);
            setIsUrlSaved(true);
          }

          const streamerId = data.streamerId;
          if (streamerId) {
            if (user && streamerId === user.playerId) {
              setStreamerName(user.username);
            } else {
              const players = await getPlayers(streamerId, accessToken);
              if (players && players.length > 0) {
                setStreamerName(players[0].username);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching streaming data:", error);
        setSelectedOption(null);
      }
    };

    fetchStreamingData();
  }, [matchId, accessToken, user, tournamentId]);

  const handleSaveUrl = async () => {
    const body = {
      matchId,
      tournamentId,
      streamerId: user.playerId,
      streamingType: "VOD",
      streamingLink: url,
    };

    try {
      if (matchStreamingId) {
        await updateStreamingByMatch(matchStreamingId, body, accessToken);
        setIsUrlSaved(true);
      } else {
        const data = await createStreamingByMatch(body, accessToken);
        setMatchStreamingId(data.matchStreamingId);
        setIsUrlSaved(true);
      }
    } catch (error) {
      console.error("Error saving streaming:", error);
    }
  };

  const handleDateTimeChange = async (newDate) => {
    setDateTime(newDate);
    setShowDateTimePicker(false);

    const streamingType = selectedOption === "directo" ? "LIVE" : "LIVE-VOD";
    const streamingDate = newDate.toISOString();

    const body = {
      matchId,
      tournamentId,
      streamerId: user.playerId,
      streamingType,
      streamingDate,
    };

    try {
      if (matchStreamingId) {
        await updateStreamingByMatch(matchStreamingId, body, accessToken);
      } else {
        const data = await createStreamingByMatch(body, accessToken);
        setMatchStreamingId(data.matchStreamingId);
      }
    } catch (error) {
      console.error("Error saving streaming:", error);
    }
  };

  const deleteStreaming = async () => {
    if (matchStreamingId) {
      try {
        await deleteStreamingByMatch(matchStreamingId, accessToken);
        setMatchStreamingId(null);
        setSelectedOption(null);
        setDateTime(null);
        setShowDateTimePicker(false);
        setUrl("");
        setIsUrlSaved(false);
        setStreamerName("");
      } catch (error) {
        console.error("Error deleting streaming:", error);
      }
    } else {
      setSelectedOption(null);
      setDateTime(null);
      setShowDateTimePicker(false);
      setUrl("");
      setIsUrlSaved(false);
      setStreamerName("");
    }
  };

  const handleFutureSelectedOption = (option) => {
    setSelectedOption(option);
    setShowDateTimePicker(true);
    setStreamerName(user.username);
  };

  return {
    selectedOption,
    setSelectedOption,
    dateTime,
    showDateTimePicker,
    setShowDateTimePicker,
    url,
    setUrl,
    isUrlSaved,
    matchStreamingId,
    streamerName,
    isStreamer,
    handleSaveUrl,
    handleDateTimeChange,
    deleteStreaming,
    handleFutureSelectedOption,
  };
};

export default useStreamingData;
